import { SelectableObject, SelectableWithIdObject } from "src/components/Form/types";
import type { AvailableCoursesResponse, RelationInfo, RelationTreeResponse } from "src/shared/api-types/course";

import request from "./request";
import { typedRequest } from "./typedRequest";

interface CourseBase {
  id?: number; // Optional for requests (server-generated on create, URL param on update)
  name: string;
  shortName: string;
  displayName: string;
  type: string;
  startDate: number;
  summary: string | null;
  summary2?: string | null;
  visible: number;
  showAtAllCourses: number;
  isConfigured: boolean;
}

export interface CourseResponse extends CourseBase {
  buffer?: number;
  price?: string;
  refreshers?: RefresherCourseResponse[];
  validPeriod?: number;
  validPeriodId?: number;
  courseCategory?: string;
  courseCategoryId?: number;
  isPartOfTraining?: boolean;
  isRefresher?: boolean;
}

export interface RefresherCourseResponse extends CourseBase {
  isActive: number;
  buffer?: number;
  price?: string;
  season?: string | null;
}

export interface CourseRequest extends CourseBase {
  crewId?: string;
  bufferZone?: number | string | null;
  price?: number;
  validPeriodId: number | string | null;
  refreshers?: { id: number; isActive: number; season?: string | null }[];
  positionValidPeriods?: { positionId: number; validPeriodId: number }[];
  courseCategoryId: number | string | null;
  isRefresher?: boolean;
}

export interface CompanyCourseRequest extends Omit<CourseRequest, "price"> {
  authorizedSignatoryId?: number | string | null;
}

interface CourseShortResponse {
  id: number;
  displayname: string;
}

export class CourseShort extends SelectableWithIdObject {
  public displayname: string;

  constructor(data: CourseShortResponse) {
    super(data.id);
    this.displayname = data.displayname;
  }

  getDisplayName(): string {
    return `(${this.id}) ${this.displayname}`;
  }
}

export const mapCourseShort = (data: CourseShortResponse): CourseShort => {
  return new CourseShort(data);
};

/**
 * Server-assembled course relation tree (roles → positions → operations with
 * aircraft nesting) plus the flat relations catalog for form dropdowns.
 * `includeHidden` merges hidden relations into the tree, stamped `hidden: true`.
 * Without `crewId` (super admin) the tree covers system-level relations only.
 */
export const getRelationTree = async (
  crewId: number | null = null,
  includeHidden = false,
): Promise<RelationTreeResponse> => {
  const params = new URLSearchParams();
  if (crewId) params.set("crewId", String(crewId));
  if (includeHidden) params.set("includeHidden", "true");
  const query = params.toString();
  return typedRequest.get<never, RelationTreeResponse>({
    url: `/courses/relation-tree${query ? `?${query}` : ""}`,
  });
};

export const getMoodleCourses = async (): Promise<CourseResponse[]> => {
  const json = await request(`/courses/moodle`);
  return json;
};

export class Season extends SelectableObject {
  constructor(private season: string) {
    super();
  }

  getId(): string {
    return this.season;
  }

  getDisplayName(): string {
    return this.season;
  }
}

export const getRefresherSeasons = async (): Promise<Season[]> => {
  const json = await request(`/courses/refresher-seasons`);
  return json.map((season: string) => new Season(season));
};

export const getCustomCourses = async () => {
  const json = await request(`/courses/custom`);
  return json;
};

export const getCompanyCourses = async (crewId: string) => {
  const json = await request(`/courses/custom/crew/${crewId}`);
  return json;
};

export const getCustomCourseById = async (courseId: string) => {
  const json = await request(`/courses/custom/${courseId}`);

  return json;
};

export const getMoodleCourseById = async (courseId: string): Promise<CourseResponse> => {
  const json = await request(`./courses/moodle/${courseId}`);

  return json;
};

export const deleteCustomCourseById = async (courseId: string) => {
  const json = await request(`./courses/custom/${courseId}`, "DELETE");

  return json;
};

export const updateCustomCourse = async (courseId: string, newCourse: CourseRequest) => {
  const crew = newCourse.crewId ? `/${newCourse.crewId}` : "";
  const response = await request(`/courses/custom/${courseId}${crew}`, "PUT", newCourse);

  return response;
};

export const addMoodleCourse = async (newCourse: CourseRequest) => {
  const response = await request(`/courses/moodle`, "POST", newCourse);
  return response;
};

export const updateMoodleCourse = async (courseId: number, newCourse: CourseRequest) => {
  const response = await request(`/courses/moodle/${courseId}`, "PUT", newCourse);

  return response;
};

export const addCustomCourse = async (newCourse: CompanyCourseRequest) => {
  const crew = newCourse.crewId ? `/${newCourse.crewId}` : "";
  const response = await request(`/courses/custom${crew}`, "POST", newCourse);
  return response;
};

export const getCourse = async (courseId: number, isCustom: boolean) => {
  let url = `/courses/${courseId}`;
  if (isCustom) {
    url += "?custom=true";
  }
  const json = await request(url);
  return json;
};

export interface CourseVisibilityToggle extends RelationInfo {
  id: number;
  groupId?: number;
  isCustom?: boolean;
}

type CourseRelationCrewId = number | null;

const courseRelationUrl = (crewId: CourseRelationCrewId) =>
  !crewId ? `/courses/relation` : `/courses/relation/${crewId}`;

export const toggleCourseVisibility = async (crewId: number, course: CourseVisibilityToggle) => {
  const json = await request.post(`/courses/toggle/${crewId}`, {
    id: course.id,
    groupId: course.groupId,
    isCustom: course.isCustom,
    roleId: course.roleId,
    positionId: course.positionId,
    operationId: course.operationId,
    designatorId: course?.designatorId,
    aircraftTypeId: course?.aircraftTypeId,
  });

  return json;
};

export const getAvailableCourses = async (crewId: number): Promise<AvailableCoursesResponse> => {
  const json = await request(`/courses/available/${crewId}`);
  return json;
};

export interface CourseRelationInput extends RelationInfo {
  id?: number;
  isCustom?: boolean;
}

export const createCourseRelation = async (crewId: CourseRelationCrewId, relation: CourseRelationInput) => {
  const url = courseRelationUrl(crewId);
  const json = await request.post(url, relation);
  return json;
};

export const deleteRelation = async (crewId: CourseRelationCrewId, relation: CourseRelationInput) => {
  const url = courseRelationUrl(crewId);
  const id = relation.groupId || relation.id;
  const isGroup = !!relation.groupId;
  let query = `id=${id}`;
  if (isGroup) {
    query += `&isGroup=true`;
  } else if (relation.isCustom) {
    query += `&isCustom=true`;
  }
  if (relation.roleId) query += `&roleId=${relation.roleId}`;
  if (relation.positionId) query += `&positionId=${relation.positionId}`;
  if (relation.operationId) query += `&operationId=${relation.operationId}`;
  if (relation.designatorId) query += `&designatorId=${relation.designatorId}`;
  if (relation.aircraftTypeId) query += `&aircraftTypeId=${relation.aircraftTypeId}`;

  const json = await request.delete(`${url}?${query}`);
  return json;
};

export const getMoodleActiveCourses = async () => {
  const response = await request("/courses/active");
  return response;
};

export const getMoodleCoursesForTraining = async (): Promise<CourseShort[]> => {
  const response = await request("/courses/training");
  return Array.isArray(response) ? response.map(mapCourseShort) : [];
};

// Company LMS Courses API calls
export const getCompanyCoursesForCrew = async (crewId: number): Promise<CourseResponse[]> => {
  return await request(`/courses/companies/crew/${crewId}`);
};

export const getCompanyCourseById = async (crewId: number, courseId: string): Promise<CourseResponse> => {
  return await request(`/courses/companies/${crewId}/${courseId}`);
};

export const createCompanyCourse = async (crewId: number, newCourse: CompanyCourseRequest) => {
  return await request(`/courses/companies/${crewId}`, "POST", newCourse);
};

export const updateCompanyCourse = async (
  crewId: number,
  courseId: string,
  newCourse: Partial<CompanyCourseRequest>,
) => {
  return await request(`/courses/companies/${crewId}/${courseId}`, "PUT", newCourse);
};

export const archiveCompanyCourse = async (crewId: number, courseId: string) => {
  return await request(`/courses/companies/${crewId}/${courseId}`, "DELETE");
};
